/**
 * Code block component with syntax highlighting and copy functionality
 * Renders inline code or full code blocks with language detection
 */

import { useCopy } from "@hooks:useCopy";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuCheck, LuCopy } from "react-icons/lu";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

import { MARKDOWN_STYLES } from "./markdown/constants";
import { extractCodeString, extractLanguage } from "./markdown/utils";

/**
 * Props for CodeBlock component
 */
interface CodeBlockProps {
  /**
   * Whether this is inline code (backtick) or code block (triple backtick)
   */
  inline?: boolean;

  /**
   * CSS class name from markdown (e.g., "language-python")
   */
  className?: string;

  /**
   * Code content as React children
   */
  children: React.ReactNode;
}

/**
 * Renders syntax-highlighted code blocks with copy button
 * For inline code, renders simple styled code element
 *
 * @param props - CodeBlock component props
 * @returns Rendered code element
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({
  inline,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const { copy, isCopied } = useCopy();

  // Extract code string from children
  const code = useMemo(() => extractCodeString(children), [children]);

  // Extract or detect language
  const language = useMemo(
    () => extractLanguage(className, code),
    [className, code],
  );

  // Inline code: simple styled element
  if (inline) {
    return (
      <Box component="code" sx={MARKDOWN_STYLES.inlineCode}>
        {children}
      </Box>
    );
  }

  // Block code: syntax highlighted with copy button
  return (
    <Box sx={MARKDOWN_STYLES.codeBlock}>
      <Stack sx={MARKDOWN_STYLES.codeBlockHeader}>
        <Chip
          label={language}
          size="small"
          sx={{
            fontSize: "0.75rem",
            height: "24px",
            textTransform: "uppercase",
          }}
        />
        <Tooltip title={isCopied ? t("common.copied") : t("chat.copyCode")}>
          <IconButton
            size="small"
            onClick={() => copy(code)}
            sx={{
              width: "32px",
              height: "32px",
            }}
          >
            {isCopied ? (
              <LuCheck size={18} color="#2e7d32" />
            ) : (
              <LuCopy size={18} />
            )}
          </IconButton>
        </Tooltip>
      </Stack>

      <SyntaxHighlighter
        language={language}
        style={oneLight}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: "12px",
          backgroundColor: "#f6f8fa",
          fontSize: "14px",
          lineHeight: "1.6",
          borderRadius: "4px",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
};
